import { useEffect, useRef, useState } from 'react'
import { companyProfileFields, getCompanyBusinessProfile, getCompanyEnrichmentStatus, saveCompanyBusinessProfile, searchCompanies,
  type CompanyField, type CompanyFields, type CompanyMatch, type CompanyProfileConfirmation } from '../../services/companyEnrichment'
import { changeOrganizationEnrichment, type OrganizationEnrichment } from '../../services/companyEnrichment'

export function CompanyProfileEnrichment({ orgId, onConfirmDraft }: { orgId?: string; onConfirmDraft?: (confirmation: CompanyProfileConfirmation) => void }) {
  const [status, setStatus] = useState<Awaited<ReturnType<typeof getCompanyEnrichmentStatus>> | null>(null)
  const [query, setQuery] = useState('')
  const [providerId, setProviderId] = useState('')
  const [matches, setMatches] = useState<CompanyMatch[]>([])
  const [selected, setSelected] = useState<CompanyMatch | null>(null)
  const [lookupKey, setLookupKey] = useState('')
  const [fields, setFields] = useState<CompanyFields>({})
  const [confirmedFields, setConfirmedFields] = useState<CompanyFields>({})
  const [preparation,setPreparation]=useState<Awaited<ReturnType<typeof getCompanyBusinessProfile>>['preparation']>(null)
  const [checked, setChecked] = useState<CompanyField[]>([])
  const [revision, setRevision] = useState<number | null>(null)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [enrichment, setEnrichment] = useState<OrganizationEnrichment | null>(null)
  const [enrichmentReload, setEnrichmentReload] = useState(0)
  const loadedLookup = useRef('')
  const reviewStarted = useRef(false)
  useEffect(() => {
    if (!orgId) return
    let current = true
    let timer: ReturnType<typeof setTimeout>
    loadedLookup.current = ''; setEnrichment(null)
    const poll = async () => {
      try {
        const value = await getCompanyBusinessProfile(orgId)
        if (!current) return
        const job = value.enrichment
        setEnrichment(job || null)
        if (!reviewStarted.current && job?.lookupKey && job.lookupKey !== loadedLookup.current && job.matches?.length) {
          loadedLookup.current = job.lookupKey
          setLookupKey(job.lookupKey); setMatches(job.matches)
        }
        if (job?.state === 'pending' || job?.state === 'processing') timer = setTimeout(poll, 5000)
      } catch { if (current) setError('Unable to load enrichment. Reopen this panel to retry.') }
    }
    void poll()
    return () => { current = false; clearTimeout(timer) }
  }, [orgId, enrichmentReload])
  const changeEnrichment = async (action: 'retry' | 'skip') => {
    if (!orgId) return
    setBusy(true); setError('')
    try {
      const value = await changeOrganizationEnrichment(orgId, action)
      setEnrichment(value.enrichment)
      setEnrichmentReload(value => value + 1)
      if (action === 'skip') { setMatches([]); setSelected(null); setLookupKey('') }
    } catch { setError('Unable to update enrichment. Manual entry remains available.') }
    finally { setBusy(false) }
  }
  useEffect(() => {
    let current = true
    setRevision(null); setFields({}); setMatches([]); setSelected(null); setChecked([]); setLookupKey('')
    reviewStarted.current = false
    getCompanyEnrichmentStatus().then(value => { if (current) { setStatus(value); setProviderId(value.providers[0]?.id || '') } }).catch(() => { if (current) setStatus({ enabled: false, configured: false, providers: [] }) })
    if (orgId) getCompanyBusinessProfile(orgId).then(value => {
      if (!current) return
      setRevision(value.business_profile_revision)
      const existing = Object.fromEntries(Object.entries(value.business_profile).map(([key, item]) => [key, item?.value]))
      setFields(existing); setConfirmedFields(existing)
      setPreparation(value.preparation)
    }).catch(() => { if (current) setError('Unable to load the current profile. Reopen this panel to retry.') })
    else setRevision(1)
    return () => { current = false }
  }, [orgId])
  const search = async () => {
    reviewStarted.current = true
    setBusy(true); setError(''); setMessage(''); setMatches([]); setSelected(null); setChecked([]); setFields(confirmedFields)
    try {
      const result = await searchCompanies(query, providerId)
      setLookupKey(result.lookupKey); setMatches(result.matches)
      if (!result.matches.length) setMessage('No matches. Continue with manual entry.')
    } catch { setError('Lookup unavailable. Continue with manual entry or retry later.') }
    finally { setBusy(false) }
  }
  const save = async () => {
    if (revision === null) return
    setBusy(true); setError(''); setMessage('')
    try {
      const confirmed = Object.fromEntries(checked.filter(key => fields[key] !== undefined && fields[key] !== '').map(key => [key, fields[key]]))
      if (!Object.keys(confirmed).length) { setError('Select at least one nonempty field to confirm.'); return }
      const confirmation = { expectedRevision: revision, fields: confirmed, ...(selected ? { lookupKey, matchId: selected.id } : {}) }
      if (!orgId) {
        onConfirmDraft?.(confirmation)
        setChecked([]); setMessage('Selected fields ready. Save the organization to persist the reviewed information.')
        return
      }
      const result = await saveCompanyBusinessProfile(orgId, confirmation)
      setRevision(result.business_profile_revision); setConfirmedFields(previous => ({ ...previous, ...confirmed })); setChecked([]); setMessage('Selected fields confirmed. This does not verify business ownership.')
    } catch { setError('Unable to save. The profile or provider authorization may have changed. Reopen the panel and review before retrying.') }
    finally { setBusy(false) }
  }
  return <section className="stack" aria-label="Company business profile">
    <strong>Company business profile</strong>
    {preparation?<div><p>Agency-prepared suggestions require company review. Suggested industry: {preparation.suggestedIndustryKey?.replace(/_/g,' ')||'Not supplied'}. Review the industry template in setup before applying.</p>
      <ul>{preparation.proposedLocations?.map((site,index)=><li key={index}>Proposed site: {site.name} — {site.address||'Address to review'}</li>)}</ul>
      <p>Proposed sites are not operational locations or access grants. An authorized company administrator can configure them in Locations after review.</p></div>:null}
    <p>Review public suggestions or enter details manually. Select each field you approve before saving. Existing confirmed fields change only when selected. Public listings do not prove ownership.</p>
    {orgId && enrichment ? <div>
      <p role="status">Organization enrichment: {enrichment.state}{enrichment.reasonCode ? ` (${enrichment.reasonCode})` : ''}</p>
      <button type="button" disabled={busy} onClick={() => changeEnrichment('skip')}>Skip enrichment</button>
      <button type="button" disabled={busy || !status?.enabled || enrichment.state === 'pending' || enrichment.state === 'processing'} onClick={() => changeEnrichment('retry')}>Retry enrichment</button>
    </div> : null}
    {!status ? <p role="status">Loading lookup configuration…</p> : !status.enabled ? <p>Company lookup is disabled. Manual entry remains available.</p> : !status.configured ? <p>No authorized business provider is configured. Manual entry remains available.</p> : <div>
      <label>Company to search<input className="input" value={query} onChange={event => setQuery(event.target.value)} maxLength={160} /></label>
      <label>Business provider<select value={providerId} onChange={event => setProviderId(event.target.value)}>{status.providers.map(provider => <option key={provider.id} value={provider.id}>{provider.id}</option>)}</select></label>
      <button type="button" disabled={busy || query.trim().length < 2} onClick={search}>{busy ? 'Looking up…' : 'Search companies'}</button>
    </div>}
    {matches.map(match => <div key={match.id}>
      <button type="button" disabled={busy} onClick={() => { reviewStarted.current = true; setSelected(match); setChecked([]); setFields({ ...confirmedFields, ...match.fields }) }}>
        Select {match.fields.legalName || match.fields.tradingName || match.id} — {[match.fields.city, match.fields.region, match.fields.country, match.fields.website].filter(Boolean).join(', ')}
      </button>
      <p>{match.attribution} · Unverified · Retrieved {match.retrievedAt} · Confidence {match.confidence === null ? 'not supplied' : match.confidence}</p>
      <a href={match.sourceUrl} target="_blank" rel="noopener noreferrer">View business listing source</a>
    </div>)}
    {selected ? <p>Reviewing {selected.id} · {selected.attribution}. <button type="button" onClick={() => { setSelected(null); setChecked([]); setFields(confirmedFields) }}>Use manual entry</button></p> : null}
    <fieldset disabled={busy || revision === null}>
      <legend>Fields to confirm</legend>
      {companyProfileFields.map(key => <label key={key} style={{ display: 'block' }}>
        <input type="checkbox" aria-label={`Confirm ${key}`} checked={checked.includes(key)} onChange={event => setChecked(previous => event.target.checked ? [...previous, key] : previous.filter(item => item !== key))} /> Confirm {key}
        <input className="input" aria-label={key} type={key === 'latitude' || key === 'longitude' ? 'number' : 'text'} step="any" value={fields[key] ?? ''}
          onChange={event => setFields(previous => ({ ...previous, [key]: event.target.value === '' ? '' : key === 'latitude' || key === 'longitude' ? Number(event.target.value) : event.target.value }))} />
      </label>)}
      <button type="button" disabled={!checked.length} onClick={save}>{orgId ? 'Save confirmed fields' : 'Use confirmed fields'}</button>
    </fieldset>
    {message ? <p role="status">{message}</p> : null}
    {error ? <p role="alert">{error}</p> : null}
  </section>
}
